/*@jsxRuntime automatic @jsxImportSource react*/
/*Unfortunately, this is entirely opaque to the user at the moment; there's no error information in the UI.*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1741219200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Mar 06 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "clickhouse",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose authentication method",
          "depth": 1,
          "id": "choose-authentication-method"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Table selector fails to populate",
          "depth": 1,
          "id": "table-selector-fails-to-populate"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: "Hightouch only supports ClickHouse v20 and newer."
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in ", _jsx(_components.a, {
          href: "https://clickhouse.com/",
          children: "ClickHouse"
        }), " and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to ClickHouse for information like your host, port, database name, and credentials."]
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "ClickHouse"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-authentication-method",
        children: "Choose authentication method"
      }), "\n", _jsxs(_components.p, {
        children: ["You can select to use either a ", _jsx(_components.strong, {
          children: "password"
        }), " or ", _jsx(_components.strong, {
          children: "connection string"
        }), ". A ", _jsx(_components.strong, {
          children: "connection string"
        }), " is useful if you need to pass additional configuration options. Hightouch uses the ", _jsx(_components.a, {
          href: "https://github.com/ClickHouse/clickhouse-java/tree/main/clickhouse-jdbc",
          children: "official ClickHouse JDBC driver"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you select ", _jsx(_components.strong, {
          children: "connection string"
        }), ", enter a JDBC connection string and skip to the ", _jsx(_components.a, {
          href: "#test-your-connection",
          children: "connection test section"
        }), " of this page."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "ClickHouse"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your ClickHouse server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your ClickHouse server's HTTP API. The default is 8123, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the database to use when Hightouch executes queries in ClickHouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal ClickHouse login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up ClickHouse as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from ClickHouse."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ClickHouse source support writing queries in ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "the SQL editor"
        }), " as its modeling method."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {
        source: "true"
      }), "\n", _jsx(_components.h3, {
        id: "table-selector-fails-to-populate",
        children: "Table selector fails to populate"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["If the ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "table selector"
        }), " fails to populate, it may be that the Clickhouse user\nconfigured for the connection lacks necessary privileges. In order for Hightouch to automatically inspect the\nschema and populate the table selector, the user configured for the connection must have ", _jsx(_components.code, {
          children: "SELECT"
        }), " permissions\non ", _jsx(_components.code, {
          children: "INFORMATION_SCHEMA.COLUMNS"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
